/**
 * The pipeline as a rail: how far the run climbed before it broke. No colour carries the
 * meaning; the stretch that held is a solid line of filled nodes, the break a larger ink
 * node with a cross, everything after a dashed run of hollow nodes.
 */

import { Check, Minus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { LadderRung } from '../report-data';

const isBroken = (status: string | null) => status === 'broken';

const Node = ({ broken, held }: { broken: boolean; held: boolean }) => (
  <span
    className={cn(
      'z-10 flex items-center justify-center rounded-full border bg-white transition-all duration-500',
      broken ? 'size-6 border-ink bg-ink text-white' : 'size-5',
      !broken && held && 'border-indigo-500 bg-indigo-500 text-white',
      !broken && !held && 'border-gray-500 text-gray-800'
    )}
  >
    {broken ? (
      <X className='size-3.5' aria-hidden />
    ) : held ? (
      <Check className='size-3' aria-hidden />
    ) : (
      <Minus className='size-3' aria-hidden />
    )}
  </span>
);

export const LadderRail = ({ ladder }: { ladder: LadderRung[] }) => {
  if (!ladder.length) return null;
  const breakAt = ladder.findIndex((rung) => isBroken(rung.status));
  // No break at all means the whole climb held; everything is "before the break".
  const brokenIndex = breakAt === -1 ? ladder.length : breakAt;

  return (
    <ol className='flex items-start'>
      {ladder.map((rung, index) => {
        const broken = index === brokenIndex;
        const held = index < brokenIndex && rung.status === 'ok';
        const last = index === ladder.length - 1;
        return (
          <li key={rung.key} className='flex min-w-0 flex-1 flex-col items-center gap-1.5'>
            <div className='flex w-full items-center'>
              {/* Half-width stubs on the ends keep the first and last nodes centred over their labels. */}
              <span
                className={cn(
                  'h-px flex-1',
                  index === 0 ? 'bg-transparent' : index <= brokenIndex ? 'bg-indigo-500' : 'bg-transparent'
                )}
              />
              <Node broken={broken} held={held} />
              <span
                className={cn(
                  'h-px flex-1',
                  last
                    ? 'bg-transparent'
                    : index < brokenIndex
                      ? 'bg-indigo-500'
                      : 'border-t border-dashed border-gray-500 bg-transparent'
                )}
              />
            </div>
            <span
              title={rung.note ?? undefined}
              className={cn(
                'w-full px-0.5 text-center text-[10px] leading-3.5',
                broken ? 'font-medium text-text-heading' : 'text-gray-800'
              )}
            >
              {rung.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
};
